/**
 * StreamDecimator — целочисленная децимация hi-res потока (384→96, 192→96,
 * 176.4→88.2, 352.8→88.2 кГц) с антиалиасным windowed-sinc FIR и состоянием,
 * переносимым через границы чанков: выход побитово не зависит от нарезки
 * входа (это контракт, его держит node-тест chunk-invariance).
 *
 * Зачем: потоковый лейн мастеринга работает на РОДНОЙ частоте файла
 * (осознанно — против тихой деградации hi-res). Но DXD-мастер 384 кГц на
 * слабой машине — это 8.7× работы на каждый из 3-4 проходов лейна: замер
 * 09.08 — 60 с звука = 113 с счёта при CPU 4×, а у Pro-3450 (4 ГБ, no-SIMD)
 * его 459 МБ файл дважды не дожил до конца («даже с 1 треком заморочка»).
 * Децимация ×4 — в 4 раза меньше и работы, и памяти на всех проходах.
 * Включается ТОЛЬКО явным выбором пользователя (никакой тихой деградации).
 *
 * API:
 *   var d = StreamDecimator.create(channels, factor);   // factor ∈ {2,4,8}
 *   var o = d.push(chans, frames); // {channels:[Float32Array...], frames}
 *   var t = d.flush();             // хвост с нулевым дополнением справа
 *   StreamDecimator.pickFactor(sr) // 384000→4, 192000→2, 44100→0 (не надо)
 *
 * Фильтр: windowed-sinc (Blackman), срез 0.45·(выходной Найквист),
 * 24·factor+1 тапов — подавление алиасов >60 дБ (node-тест меряет Гёрцелем).
 */
(function (root) {
    'use strict';

    function designLowpass(factor) {
        // cutoff в долях ВХОДНОЙ частоты: 0.45 · (1/factor) · 0.5·2 = 0.45/factor
        var fc = 0.45 / factor;                 // нормированная (1.0 = Fs)
        var taps = 24 * factor + 1;             // нечётное — линейная фаза
        var h = new Float64Array(taps);
        var mid = (taps - 1) / 2, sum = 0;
        for (var i = 0; i < taps; i++) {
            var x = i - mid;
            var sinc = x === 0 ? 2 * Math.PI * fc : Math.sin(2 * Math.PI * fc * x) / x;
            // Blackman
            var w = 0.42 - 0.5 * Math.cos(2 * Math.PI * i / (taps - 1)) + 0.08 * Math.cos(4 * Math.PI * i / (taps - 1));
            h[i] = sinc * w;
            sum += h[i];
        }
        for (i = 0; i < taps; i++) h[i] /= sum;  // единичное усиление на DC
        return h;
    }

    function create(channels, factor) {
        if (factor !== 2 && factor !== 4 && factor !== 8) throw new Error('StreamDecimator: factor must be 2/4/8');
        var ch = Math.max(1, channels | 0);
        var h = designLowpass(factor);
        var taps = h.length;
        // Состояние: непереработанный хвост входа на канал. Выходной отсчёт k
        // берёт окно входа [k·f .. k·f+taps-1] (свёртка со сдвигом), поэтому
        // хвост < taps + (недобор до кратности factor) переносится в следующий push.
        var carry = [];
        for (var c = 0; c < ch; c++) carry.push(new Float32Array(0));

        function processChannel(c, data) {
            var prev = carry[c];
            var total = prev.length + data.length;
            var buf = new Float32Array(total);
            buf.set(prev, 0); buf.set(data, prev.length);
            // сколько ПОЛНЫХ выходных отсчётов можем выдать: нужно окно taps
            var nOut = Math.floor((total - taps) / factor) + 1;
            if (nOut < 0) nOut = 0;
            var out = new Float32Array(nOut);
            for (var k = 0; k < nOut; k++) {
                var base = k * factor, acc = 0;
                for (var t = 0; t < taps; t++) acc += h[t] * buf[base + t];
                out[k] = acc;
            }
            var consumed = nOut * factor;        // вход, полностью «отданный» выходу
            carry[c] = buf.slice(consumed);      // остальное — в состояние
            return out;
        }

        return {
            factor: factor,
            push: function (chans, frames) {
                var outs = [], n = 0;
                for (var c = 0; c < ch; c++) {
                    var src = chans[c] || chans[0];
                    var d = src.length === frames ? src : src.subarray(0, frames);
                    outs.push(processChannel(c, d));
                }
                n = outs[0] ? outs[0].length : 0;
                return { channels: outs, frames: n };
            },
            flush: function () {
                // дорабатываем хвост: нулевое дополнение справа до целого окна
                var outs = [], n = 0;
                for (var c = 0; c < ch; c++) {
                    var rest = carry[c];
                    if (!rest.length) { outs.push(new Float32Array(0)); continue; }
                    var pad = new Float32Array(rest.length + taps);
                    pad.set(rest, 0);
                    var nOut = Math.max(0, Math.floor((rest.length - 1) / factor) + 1);
                    var out = new Float32Array(nOut);
                    for (var k = 0; k < nOut; k++) {
                        var base = k * factor, acc = 0;
                        for (var t = 0; t < taps; t++) acc += h[t] * pad[base + t];
                        out[k] = acc;
                    }
                    carry[c] = new Float32Array(0);
                    outs.push(out);
                }
                n = outs[0] ? outs[0].length : 0;
                return { channels: outs, frames: n };
            }
        };
    }

    // Какой фактор предложить: только точные семейные пары (96k/88.2k базы).
    function pickFactor(sr) {
        if (sr % 96000 === 0 && sr / 96000 >= 2) return Math.min(8, sr / 96000);
        if (sr % 88200 === 0 && sr / 88200 >= 2) return Math.min(8, sr / 88200);
        return 0;
    }

    var API = { create: create, pickFactor: pickFactor, _designLowpass: designLowpass };
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
    if (typeof window !== 'undefined') window.StreamDecimator = API;
})(typeof globalThis !== 'undefined' ? globalThis : this);
